/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Checkbox, CircularProgress, Box, FormControl, FormControlLabel, Button, Grid, IconButton, TextField, Tooltip, Typography, Chip, Switch, FormHelperText } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';

import Page from '../../../components/Page';
import subscriptionServices from '../../../services/subscriptionServices';
import adminServices from '../../../services/adminServices';
import ErrorFocus from '../../../components/ErrorFocus/ErrorFocus';
import SnackBar from '../../../components/snackbar/snackbar';

import { SectionCard, FieldLabel, FeatureBullets, BackLink, PageHeading, SearchBox, CoverImageUpload, TierButton } from './components/shared/SharedComponents';

const BillingToggle = ({ value, onChange }) => (
  <div className="flex border border-gray-200 rounded-[7px] overflow-hidden">
    {['Yearly', 'Monthly'].map((opt) => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        className="flex-1 text-center px-4 py-[9px] text-[13px] font-medium cursor-pointer select-none transition-all"
        style={{
          background: value === opt ? '#eff6ff' : '#fff',
          color: value === opt ? '#1d4ed8' : '#374151',
          border: value === opt ? '1.5px solid #2563eb' : '1px solid transparent',
          borderRadius: value === opt ? '6px' : 0,
        }}
      >
        {opt}
      </button>
    ))}
  </div>
);

const CreateBundlePlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [courses, setCourses] = useState([]);
  const [courseSearch, setCourseSearch] = useState('');
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [submittingDraft, setSubmittingDraft] = useState(false);
  const [submittingPublish, setSubmittingPublish] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, title: '' });
  const publishModeRef = React.useRef('draft');
  const [lookups, setLookups] = useState({ subscription_ids: [], currency_type: [] });
  const fromTab = location.state?.fromTab ?? 2;
  const [coverImageError, setCoverImageError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarTitle, setSnackbarTitle] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [coverImgPreview, setCoverImgPreview] = useState(null);


  useEffect(() => { fetchCourses(); fetchLookups(); }, []);

  const fetchLookups = async () => {
    try {
      const res = await adminServices.getLookUpData('subscription_ids,currency_type');
      console.log('lookup result:', res.ok, res.data);
      if (res.ok) setLookups(res.data || {});
    } catch (e) { console.error('[CreateBundlePlan lookups]', e); }
  };

  const fetchCourses = async () => {
    setCoursesLoading(true);
    try {
      const res = await adminServices.getLookUpData(['course']);
      if (res.ok) setCourses((res.data.course || []).filter((c) => Number(c.status) === 1 && !c.isFree));
    } catch (e) { console.error('[CreateBundlePlan]', e); }
    setCoursesLoading(false);
  };

  const filteredCourses = courses.filter((c) => {
    const code = (c.audienceCode || '').toUpperCase();
    return !c.isFree && (code === 'B2C' || code === 'BOTH') && (c.title || '').toLowerCase().includes(courseSearch.toLowerCase());
  });

  const initialValues = {
    planName: '', displayName: '', description: '', tagline: '',enableonline:false,
    price: '', priceUSD: '', billingCycle: 'Yearly', billingtype: 'recurring',
    featureBullets: ['', '', ''], footnote: '', selectedCourses: []
  };



  const validationSchema =
    Yup.object().shape({
      planName: Yup.string().trim().min(3).required('Required'),
      price: Yup.number().min(0).required('Required'),
      priceUSD: Yup.number().min(0).required('Required'),
      selectedCourses: Yup.array().min(1, 'Select at least one course'),
    });

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];

    setCoverImage(null);
    setCoverImgPreview(null);
    setCoverImageError('');

    if (!file) return;

    const acceptedTypes = ['image/jpeg', 'image/png', 'image/bmp'];
    if (!acceptedTypes.includes(file.type)) {
      setCoverImageError('Only JPG, PNG, BMP allowed');
      return;
    }

    if (file.size > 104857600) {
      setCoverImageError('Max file size is 100 MB');
      return;
    }

    imageWidthAndHeight(file).then((res) => {
      if (res.width >= 1200 && res.height >= 400) {
        setCoverImage(file);
        setCoverImgPreview(URL.createObjectURL(file));
      } else {
        setCoverImageError('Image must be at least 1200 × 400 px');
      }
    });
  };

  const imageWidthAndHeight = (provideFile) => {
    if (!provideFile) {
      return Promise.resolve({ width: null, height: null });
    }
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          resolve({
            width: img.width,
            height: img.height
          });
        };

        img.src = reader.result;
      };
      reader.readAsDataURL(provideFile);
    });
  };

  const handleSubmit = async (values, publishMode = 'draft') => {
    if (!coverImage) {
      setCoverImageError('Cover image is required');
      return;
    }
    if (submittingDraft || submittingPublish) return;
    if (publishMode === 'draft') setSubmittingDraft(true);
    else setSubmittingPublish(true);
    try {
        if (publishMode === 'publish' && !values.enableonline) {
          setSnackbar({
            open: true,
            title: 'Please enable online before publishing.',
          });
          setSubmittingPublish(false)
          return false;
        }
      const fd = new FormData();
      fd.append('name', values.planName);
      fd.append('shortDescription', JSON.stringify(values.displayName ? [values.displayName] : []));
      fd.append('description', values.description || '');
      fd.append('tagline', values.tagline || '');
      fd.append('is_active', values.enableonline || '');
      fd.append('planType', 'bundle');
      fd.append('plan_category', 'COURSE_BUNDLE');
      fd.append('scope', 'platform');
      fd.append('subscriptionPlan', values.billingCycle === 'Yearly' ? 'Online Yearly Bundle' : 'Online Monthly - Bundle');
      fd.append('subscriptionType', 'B2C');
      fd.append('isOnline', publishMode === 'publish' ? 'true' : 'false');
      fd.append('isPaid', Number(values.price) > 0 ? 'true' : 'false');
      fd.append('frequency', values.billingCycle === 'Yearly' ? '1' : '2');
      fd.append('billingtype', values.billingtype);
      fd.append('courseIds', JSON.stringify(values.selectedCourses));
      fd.append('feature_bullets', JSON.stringify(values.featureBullets.filter(Boolean)));
      fd.append('footnote', values.footnote || '');
      if (coverImage) {
        fd.append('coverImage', coverImage);
      }
      const currencies = (lookups.currency_type || []).length > 0
        ? lookups.currency_type
        : [{ code: 'INR' }, { code: 'USD' }];
      fd.append('prices', JSON.stringify(
        currencies.map(c => ({
          currencyType: c.code,
          actualCost: c.code === 'INR' ? Number(values.price) || 0 : Number(values.priceUSD) || 0,
        }))
      ));
      const subIds = lookups.subscription_ids || [];
      const subName = values.billingCycle === 'Yearly' ? 'Online Yearly - Bundle' : 'Online Monthly - Bundle';
      const subscriptionId = subIds.find(s => s.name.replace(/[\s-]+/g, ' ').toLowerCase().includes(subName.replace(/[\s-]+/g, ' ').toLowerCase()) || subName.replace(/[\s-]+/g, ' ').toLowerCase().includes(s.name.replace(/[\s-]+/g, ' ').toLowerCase()))?.id;
      if (!subscriptionId) {
        setSnackbar({ open: true, title: `Subscription record "${subName}" not found. Contact admin.` });
        if (publishMode === 'draft') setSubmittingDraft(false); else setSubmittingPublish(false);
        return;
      }
      const res = await subscriptionServices.postSubscriptionPlan(subscriptionId, fd, 'bundle');
      if (res.ok) {
        navigate('/app/subscription?tab=2', { state: { message: publishMode === 'publish' ? 'Plan published!' : 'Draft saved' } });
      } else {
        setSnackbar({ open: true, title: res.data?.message || 'Something went wrong' });
      }
    } catch (e) {
      console.error('[CreateBundlePlan]', e);
      setSnackbar({ open: true, title: 'Something went wrong' });
    }
    if (publishMode === 'draft') setSubmittingDraft(false);
    else setSubmittingPublish(false);
  };

  const renderCourseList = (values, setFieldValue) => {
    if (coursesLoading) {
      return (
        <div className="flex justify-center py-8">
          <CircularProgress size={20} sx={{ color: '#2563eb' }} />
        </div>
      );
    }
    if (filteredCourses.length === 0) {
      return (
        <div className="flex justify-center py-8">
          <p className="text-[13px] text-gray-400">No courses available</p>
        </div>
      );
    }
    return filteredCourses.map((c, idx) => {
      const checked = values.selectedCourses.includes(c.id);
      return (
        <div
          key={c.id}
          className="flex items-center px-[14px] py-[10px] hover:bg-gray-50"
          style={{ borderBottom: idx < filteredCourses.length - 1 ? '1px solid #f3f4f6' : 'none' }}
        >
          <Checkbox
            size="small"
            checked={checked}
            onChange={() =>
              setFieldValue(
                'selectedCourses',
                checked
                  ? values.selectedCourses.filter((i) => i !== c.id)
                  : [...values.selectedCourses, c.id]
              )
            }
            sx={{ p: '4px', mr: '10px', color: '#d1d5db', '&.Mui-checked': { color: '#2563eb' } }}
          />
          <div>
            <p className="text-[13px] font-medium text-gray-900">{c.title}</p>
            <p className="text-[11px] text-gray-400 uppercase tracking-[0.04em]">
              {c.type || c.courseType || '—'}
              {c.isIndividuallySold && <span className="text-amber-500 ml-[6px]">· Also sold individually</span>}
            </p>
          </div>
        </div>
      );
    });
  };

  return (
    <Page title="">
      <div className="max-w-[720px] mx-auto px-4 md:px-8 py-6">
        <BackLink label="Back to Plans" onClick={() => navigate(`/app/subscription?tab=${fromTab}`)} />
        <PageHeading title="Create Bundle Plan" subtitle="Define a B2C course bundle plan with annual subscription pricing." />

        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(v) => handleSubmit(v, publishModeRef.current)}>
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
            <Form>
              <ErrorFocus />

              {/* 1 — Plan Identity */}
              <SectionCard step={1} title="Plan Identity" subtitle="Internal name, customer-facing name, and marketing copy.">
                <div className="flex flex-col gap-[18px]">
                  <div>
                    <FieldLabel required>Plan name (internal)</FieldLabel>
                    <TextField fullWidth name="planName" placeholder="e.g. Healthcare Learning Bundle" value={values.planName} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.planName && errors.planName)} helperText={touched.planName && errors.planName} size="small" />
                  </div>
                  <div>
                    <FieldLabel>Display name (customer-facing)</FieldLabel>
                    <TextField fullWidth name="displayName" placeholder="e.g. Healthcare Bundle" value={values.displayName} onChange={handleChange} onBlur={handleBlur} size="small" />
                    <p className="text-[11px] text-gray-400 mt-1">Shown on pricing page. Defaults to plan name if blank.</p>
                  </div>
                  <div>
                    <FieldLabel>Description</FieldLabel>
                    <TextField fullWidth multiline rows={3} name="description" placeholder="Briefly describe what this bundle includes..." value={values.description} onChange={handleChange} onBlur={handleBlur} size="small" />
                  </div>
                  <div>
                    <FieldLabel>Tagline</FieldLabel>
                    <TextField fullWidth name="tagline" placeholder="e.g. Master compliance in one go" value={values.tagline} onChange={handleChange} onBlur={handleBlur} size="small" />
                  </div>
                  <div>
                    <FieldLabel>Enable Online</FieldLabel>
                    <FormControlLabel
                      control={<Switch name='enableonline' checked={values.enableonline} onChange={(e) => setFieldValue('enableonline', e.target.checked)} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#2563eb' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { background: '#2563eb' } }} />}
                      label={<span className="text-[13px] text-gray-700">Mark as Active</span>}
                    />
                  </div>
                </div>
              </SectionCard>

              {/* 2 — Pricing */}
              <SectionCard step={2} title="Pricing" subtitle="Annual subscription price. Stripe Price ID is the recurring annual price.">
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <FieldLabel required>Price (₹/year)</FieldLabel>
                    <TextField fullWidth name="price" type="number" placeholder="4999" value={values.price} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.price && errors.price)} helperText={touched.price && errors.price}
                      InputProps={{ startAdornment: <span className="text-gray-500 mr-1 text-[13px]">₹</span> }} size="small" />
                  </div>
                  <div className="flex-1">
                    <FieldLabel required>Price (USD/year)</FieldLabel>
                    <TextField fullWidth name="priceUSD" type="number" placeholder="59.99"
                      value={values.priceUSD} onChange={handleChange} onBlur={handleBlur}
                      error={Boolean(touched.priceUSD && errors.priceUSD)}
                      helperText={touched.priceUSD && errors.priceUSD}
                      InputProps={{ startAdornment: <span className="text-gray-500 mr-1 text-[13px]">$</span> }}
                      size="small" />
                  </div>
                  <div className="flex-1">
                    <FieldLabel>Billing cycle</FieldLabel>
                    <BillingToggle value={values.billingCycle} onChange={(v) => setFieldValue('billingCycle', v)} />
                  </div>
                </div>
                <div>
                  <FieldLabel>Stripe Price ID</FieldLabel>
                  <TextField fullWidth name="stripePriceId" InputProps={{ readOnly: true }} placeholder="price_annual_ ..." value={values.stripePriceId} onChange={handleChange} onBlur={handleBlur} size="small" />
                  <p className="text-[11px] text-gray-400 mt-1">Recurring annual Stripe Price ID for checkout.</p>
                </div>

                <div className="mt-4">
                  <FieldLabel required>Billing Type</FieldLabel>
                  <div className="flex gap-[10px]">
                    {['recurring', 'one_time'].map((t) => (
                      <TierButton
                        key={t}
                        label={t}
                        selected={values.billingtype === t}
                        onClick={() => setFieldValue('billingtype', t)}
                      />
                    ))}
                  </div>
                </div>
              </SectionCard>

              {/* 3 — Feature Bullets */}
              <SectionCard step={3} title="Feature Bullets & Footnote" subtitle="Marketing copy for the pricing card. Max 7 bullets, 80 chars each.">
                <FieldLabel>Feature bullets</FieldLabel>
                <FieldArray name="featureBullets">
                  {({ push, remove }) => <FeatureBullets values={values} handleChange={handleChange} push={push} remove={remove} />}
                </FieldArray>
                <div className="mt-2">
                  <FieldLabel>Footnote</FieldLabel>
                  <TextField fullWidth name="footnote" placeholder="e.g. Annual subscription. Renews automatically." value={values.footnote} onChange={handleChange} onBlur={handleBlur} size="small" />
                </div>
              </SectionCard>

              {/* 5 — Courses */}
              <SectionCard step={4} title="Courses" subtitle="Select LIVE B2C courses to include in this bundle. You can add more from the plan detail page.">
                <div className="mb-2">
                  <SearchBox value={courseSearch} onChange={(e) => setCourseSearch(e.target.value)} placeholder="Search courses..." />
                </div>
                <div className={`border rounded-[7px] max-h-[240px] overflow-y-auto ${touched.selectedCourses && errors.selectedCourses ? 'border-red-400' : 'border-gray-200'}`}>
                  {renderCourseList(values, setFieldValue)}
                </div>
                {touched.selectedCourses && errors.selectedCourses && (
                  <p className="text-[11px] text-red-500 mt-1">{errors.selectedCourses}</p>
                )}
              </SectionCard>


              {/* 4 — Cover Image */}
              <SectionCard step={5} title="Cover Image" subtitle="Min 1200 × 400 px · JPG, PNG or BMP · Max 100 MB">
                <CoverImageUpload
                  existingUrl={null}
                  coverImage={coverImage}
                  preview={coverImgPreview}
                  onFileChange={handleFileChange}
                  onRemove={() => { setCoverImage(null); setCoverImgPreview(null); setCoverImageError(''); }}
                  error={coverImageError}
                />
              </SectionCard>

              {/* Footer */}
              <div className="flex items-center justify-between pt-2 pb-10">
                <button type="button" onClick={() => navigate('/app/subscription?tab=2')}
                  className="text-[13px] font-semibold text-[#374151] cursor-pointer px-5 py-[6px] rounded-[8px] border border-[#d1d5db] bg-white hover:border-[#9ca3af] hover:bg-[#f9fafb] transition-all duration-150">
                  Cancel
                </button>
                <div className="flex gap-[10px]">
                  <LoadingButton
                    type="submit"
                    loading={submittingDraft}
                    variant="outlined"
                    onClick={() => { publishModeRef.current = 'draft'; }}
                    sx={{ fontSize: '13px', fontWeight: 600, borderRadius: '8px', borderColor: '#d1d5db', color: '#374151', textTransform: 'none', px: '20px' }}>
                    Save as Draft
                  </LoadingButton>
                  {/* <LoadingButton
                    type="submit"
                    loading={submittingPublish}
                    variant="contained"
                    onClick={() => { publishModeRef.current = 'publish'; }}
                    sx={{ fontSize: '13px', fontWeight: 600, borderRadius: '8px', background: '#2563eb', textTransform: 'none', px: '20px', '&:hover': { background: '#1d4ed8' } }}>
                    Publish Plan
                  </LoadingButton> */}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <SnackBar open={snackbar.open} snackbarTitle={snackbar.title} close={() => setSnackbar({ open: false, title: '' })} />
    </Page>
  );
};

export default CreateBundlePlan;